"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.useCourierSelection = useCourierSelection;
var _checkoutUtils = require("@ori/checkout-utils");
var _react = require("react");
var _useCouriersQuery = require("../../api/useCouriersQuery");
var _contexts = require("../../contexts");
var _addressFormContext = require("../../contexts/addressFormContext");
var _useAnalytics = require("../../hooks/useAnalytics");
var _logger = require("../../logger");
var _models = require("../../models");
var _changeActiveDelivery = require("../../utils/changeActiveDelivery");
var _getCourierNameFromElement = require("../../utils/getCourierNameFromElement");
function useCourierSelection(param) {
    let { activeCourier, currentAddressId, isRefreshPending, isSelectedDeliveryLoading, setActiveCourier, setIsDeliveryChanged, setIsRefreshPending } = param;
    const isLoading = isSelectedDeliveryLoading || isRefreshPending;
    const currentCouriers = (0, _react.useRef)(undefined);
    const { couriers, isCouriersLoading } = (0, _useCouriersQuery.useCouriersQuery)({
        addressId: isLoading ? undefined : currentAddressId,
        activeCourier,
        setActiveCourier,
        setIsDeliveryChanged,
        shouldCheckValidCourier: true
    });
    // temporary caching to not display skeleton when we already had something loaded
    // the temporary skeleton created an ugly effect in UI when switching couriers
    if (couriers) {
        currentCouriers.current = couriers;
    }
    const { isSubscriptionsPage } = (0, _contexts.useCheckoutDeliveryContext)();
    const { currentCourierName, originalCourierName } = (0, _useAnalytics.useAnalytics)();
    const { isAddressFormOpen } = (0, _addressFormContext.useAddressForm)();
    const activateCourier = (0, _react.useCallback)(async (info)=>{
        if (!setIsDeliveryChanged || !setActiveCourier || !setIsRefreshPending) {
            return;
        }
        let courierName;
        let deliveryChannelId;
        if ('nativeEvent' in info) {
            // older solution sends ChangeEvent, so the data is parsed from its target
            const { target } = info.nativeEvent;
            courierName = (0, _getCourierNameFromElement.getCourierNameFromElement)(target);
            deliveryChannelId = Number(target.value);
        } else {
            // newer solution sends the needed data directly in an object
            courierName = info.courierName;
            deliveryChannelId = info.deliveryChannelId;
        }
        (0, _logger.logDebug)('Courier selected:', {
            courierName,
            deliveryChannelId
        });
        // analytics
        const metaData = [
            _models.MetaHierarchy.HomeDeliveryCourierSelection,
            _models.MetaHierarchy.NewCourierSelection
        ];
        if (courierName) {
            currentCourierName.current = courierName;
            metaData.push(courierName);
        }
        (0, _checkoutUtils.dispatchUserActionEvent)(metaData);
        // change active delivery channel / courier
        setActiveCourier(deliveryChannelId);
        if (!isSubscriptionsPage) {
            setIsRefreshPending(true);
            try {
                await (0, _changeActiveDelivery.changeActiveDelivery)({
                    deliveryChannelId,
                    setIsDeliveryChanged
                });
            } catch (error) {
                (0, _checkoutUtils.showErrorNotification)();
                setIsDeliveryChanged(true);
                _logger.mainLogger.error(error, {
                    action: "changeActiveDelivery after click on courier with deliveryChannelId: ".concat(deliveryChannelId)
                });
            }
        }
    }, [
        currentCourierName,
        isSubscriptionsPage,
        setActiveCourier,
        setIsDeliveryChanged,
        setIsRefreshPending
    ]);
    // analytics setup
    (0, _react.useEffect)(()=>{
        if (originalCourierName.current !== undefined) {
            return;
        }
        const foundCourier = couriers === null || couriers === void 0 ? void 0 : couriers.find((courier)=>courier.deliveryChannelId === activeCourier);
        const { name } = foundCourier !== null && foundCourier !== void 0 ? foundCourier : {};
        if (!name) {
            return;
        }
        // eslint-disable-next-line react-compiler/react-compiler -- want this to be modified here once at start
        currentCourierName.current = name;
        originalCourierName.current = name;
        (0, _logger.logDebug)('Previously selected courier name was stored for analytics:', name);
    }, [
        activeCourier,
        couriers,
        currentCourierName,
        originalCourierName
    ]);
    return {
        activateCourier,
        couriers: couriers !== null && couriers !== void 0 ? couriers : currentCouriers.current,
        isAddressFormOpen,
        isCouriersLoading
    };
}
